/**
 * 每日题组：每天固定 20 道，做完等明天。
 *
 * 题目来自服务端题单（源头是 puzzle-lab 人工编排的 daily20_plan_*.json）。
 * 服务端只认【当天】，难度元数据与未来日期不暴露（plan v2 §1）。
 *
 * 题面按需取，一次一道：
 *   GET /api/daily          → 今天有哪几个 slot（不含任何题面）
 *   GET /api/puzzle?slot=N  → 第 N 道，封装过，用 sealing.js 解
 * 这样玩家手上任何时刻只有他正在玩的那一道题，不会开局就拿到当天全套。
 *
 * 首屏例外：开局要玩的第一道题是确定的（本地进度算出来的第一个未解出序号），
 * 与其等 fetchDaily 回来再单独发一次 fetchPuzzle（国内访问海外服务器时这一趟
 * 往返很贵），不如把 solved 序号带给 /api/daily，服务端顺手把那一道也封装好
 * 一起吐回来（依然只吐一道，不是当天全套）。见 unsealNext。
 *
 * 无当日题单时 `ready` 为 false，页面展示"准备中"占位，**不做本地兜底选题**：
 * 兜底会让当天数据脱离题单口径，宁缺毋滥。
 *
 * 「今天」以服务端日期为准（响应里的 date），遥测的 date 字段必须用它 ——
 * 本地时区日期仅用于进度存储（storage.js）。
 */

import { unseal } from './sealing.js';

export const PUZZLES_PER_DAY = 20;

/**
 * @typedef {object} DailyPlan
 * @property {boolean} ready  当天是否有题单
 * @property {string} date    题单日期（服务器时区，YYYY-MM-DD）
 * @property {number[]} [slots] 今天的题号，升序
 */

/**
 * 取当天题号列表。网络/服务异常时抛错，由调用方给玩家一个可重试的出口。
 * @param {number[]} [solved] 本地已解出的序号（今天题组内的 0..9），用来让服务端
 *   顺带把下一道该玩的题封装好一起带回来，省一次往返。不传就只要 slot 列表。
 * @returns {Promise<DailyPlan & {next?: {slot: number, sealed: string}}>}
 */
export async function fetchDaily(solved = []) {
    const qs = solved.length ? `?solved=${solved.join(',')}` : '';
    const res = await fetch(`/api/daily${qs}`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`daily fetch failed: HTTP ${res.status}`);
    const plan = await res.json();
    if (plan.ready && Array.isArray(plan.slots)) plan.slots.sort((a, b) => a - b);
    return plan;
}

/**
 * 取第 slot 道题的题面。
 * @param {number} slot
 * @returns {Promise<{slot: number, size: string, sbn: string}>}
 */
export async function fetchPuzzle(slot) {
    const res = await fetch(`/api/puzzle?slot=${slot}`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`puzzle fetch failed: HTTP ${res.status}`);
    const { date, sealed } = await res.json();
    return unseal(sealed, date, slot);
}

/**
 * 解开 fetchDaily 顺带带回来的 next（没有就返回 null），跟 fetchPuzzle 的返回同形状。
 * @param {DailyPlan & {next?: {slot: number, sealed: string}}} plan
 * @returns {Promise<{slot: number, size: string, sbn: string} | null>}
 */
export async function unsealNext(plan) {
    if (!plan.next) return null;
    return unseal(plan.next.sealed, plan.date, plan.next.slot);
}

/**
 * 本地日期键，形如 2026-07-30。仅用于本地进度存储，遥测日期用服务端的。
 * @param {Date} [d]
 * @returns {string}
 */
export function todayKey(d = new Date()) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * 本地化的日期短文本，如 "Jul 30" / "7月30日"。
 * @param {string} lang
 * @param {Date} [d]
 */
export function formatDate(lang, d = new Date()) {
    try {
        return new Intl.DateTimeFormat(lang, { month: 'short', day: 'numeric' }).format(d);
    } catch {
        return todayKey(d).slice(5); // 兜底：MM-DD
    }
}
